import { useRef, useState } from 'react';
import { Button, Select, Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowUploadRegular, DismissRegular, ArrowClockwiseRegular, DocumentRegular } from '@fluentui/react-icons';
import { useTranslation } from '../../i18n';
import {
  addFiles, formatBytes, patch, runLimited, savedNotice, statusOf,
  type QueuedFile, type SavedFile, type UploadRules, type UploadType,
} from './uploadQueue';

/**
 * Drag-and-drop, multi-file upload — the fleet primitive (docs/upload-primitive.md).
 *
 * One endpoint call per file, because that is what every upload endpoint in the fleet takes. Files
 * are STAGED first so each one's type can be checked (U2), refused files are said up front and never
 * sent (U3), duplicates are flagged (U4), and each file reports its own outcome with its own Retry
 * (U5). The drop zone is an enhancement: "Choose files" is always there, and is the keyboard path.
 */

const useStyles = makeStyles({
  zone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThick} dashed ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground2,
  },
  zoneActive: {
    border: `${tokens.strokeWidthThick} dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  list: {
    listStyleType: 'none',
    margin: `${tokens.spacingVerticalS} 0 0 0`,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  name: { flexGrow: 1, minWidth: '160px', overflowWrap: 'anywhere' },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  bad: { color: tokens.colorPaletteRedForeground1 },
  warn: { color: tokens.colorPaletteDarkOrangeForeground1 },
  good: { color: tokens.colorPaletteGreenForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
});

export interface MultiFileUploadProps {
  types: readonly UploadType[];
  rules: UploadRules;
  /** Names of the documents already on the record, for duplicate flags (U4). */
  existingNames: readonly string[];
  /**
   * Sends ONE file. Resolve ONLY once the record provably exists (U7: read it back), and throw to fail
   * it; the message is shown on that file's row. A resolve is what makes the row leave the list as saved.
   */
  upload: (file: File, type: string) => Promise<unknown>;
  /** Turns a thrown error into the sentence shown on the row. */
  describeError?: (e: unknown) => string;
  disabled?: boolean;
  /** Files in flight at once. */
  concurrency?: number;
}

export function MultiFileUpload({
  types, rules, existingNames, upload, describeError = (e) => (e instanceof Error ? e.message : String(e)),
  disabled, concurrency = 2,
}: MultiFileUploadProps) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [running, setRunning] = useState(false);

  const add = (files: readonly File[]) => {
    if (disabled || files.length === 0) return;
    const ctx = { rules, types, existingNames };
    // The queue write is functional so a drop mid-upload cannot overwrite a status the runner just
    // set; the notice is the same pure function read against what this render saw.
    setQueue((q) => addFiles(q, files, ctx).queue);
    setNotice(addFiles(queue, files, ctx).notice);
  };

  const send = async (items: readonly QueuedFile[]) => {
    setRunning(true);
    setNotice(null);
    const saved: SavedFile[] = [];
    const savedKeys = new Set<string>();
    try {
      await runLimited(items, concurrency, async (item) => {
        const attempts = item.attempts + 1;
        setQueue((q) => patch(q, item.key, { phase: 'uploading', error: null, attempts }));
        try {
          await upload(item.file, item.type!);
          setQueue((q) => patch(q, item.key, { phase: 'uploaded' }));
          saved.push({ name: item.file.name, retried: attempts > 1 });
          savedKeys.add(item.key);
        } catch (e) {
          setQueue((q) => patch(q, item.key, { phase: 'failed', error: describeError(e) }));
          throw e;
        }
      });
    } finally {
      // U8 HAND-OFF: a confirmed file now lives in the saved documents list, so its row leaves the
      // staging list and the page names what was saved. A row left reading "Uploaded" with a dead
      // "Upload 0 files" button beside it is the state Elvia was stuck in on 2026-10-21.
      setQueue((q) => q.filter((x) => !savedKeys.has(x.key)));
      setNotice(savedNotice(saved));
      setRunning(false);
    }
  };

  const { t } = useTranslation();
  const ready = queue.filter((q) => statusOf(q) === 'ready');
  const accept = rules.extensions.map((e) => `.${e}`).join(',');

  return (
    <div>
      <div
        role="group"
        aria-label={t('upload.zone')}
        className={mergeClasses(styles.zone, over && !disabled && styles.zoneActive)}
        onDragEnter={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          add(Array.from(e.dataTransfer?.files ?? []));
        }}
      >
        <ArrowUploadRegular aria-hidden />
        <Text>{t('upload.drag')}</Text>
        <Button size="small" appearance="primary" disabled={disabled} onClick={() => inputRef.current?.click()}>
          {t('upload.choose')}
        </Button>
        <Text size={200} className={styles.muted}>
          {t('upload.limits', { extensions: rules.extensions.join(', '), size: formatBytes(rules.maxBytes) })}
        </Text>
        <input
          ref={inputRef}
          data-testid="multi-file-input"
          type="file"
          multiple
          accept={accept}
          hidden
          onChange={(e) => {
            add(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>

      <div aria-live="polite">
        {notice ? <Text block size={200} className={styles.muted} style={{ marginTop: '4px' }}>{notice}</Text> : null}
      </div>

      {queue.length > 0 ? (
        <>
          <ul className={styles.list} aria-label={t('upload.list')}>
            {queue.map((q) => {
              const status = statusOf(q);
              const editable = status !== 'uploading' && status !== 'uploaded' && status !== 'invalid';
              return (
                <li key={q.key} className={styles.row} aria-label={q.file.name}>
                  <DocumentRegular aria-hidden />
                  <Text className={styles.name}>
                    {q.file.name} <Text size={200} className={styles.muted}>{formatBytes(q.file.size)}</Text>
                  </Text>
                  {status !== 'invalid' ? (
                    <Select
                      size="small"
                      aria-label={t('upload.typeFor', { name: q.file.name })}
                      value={q.type ?? ''}
                      disabled={!editable || running}
                      onChange={(_, d) => setQueue((qs) => patch(qs, q.key, { type: d.value || null }))}
                    >
                      <option value="">{t('upload.chooseType')}</option>
                      {types.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Select>
                  ) : null}
                  <StatusText q={q} styles={styles} />
                  {status === 'duplicate' ? (
                    <Button size="small" onClick={() => setQueue((qs) => patch(qs, q.key, { allowDuplicate: true }))}>
                      {t('upload.anyway')}
                    </Button>
                  ) : null}
                  {status === 'failed' ? (
                    <Button size="small" icon={<ArrowClockwiseRegular />} disabled={running} onClick={() => void send([q])}>
                      {t('upload.retry')}
                    </Button>
                  ) : null}
                  {status !== 'uploading' ? (
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label={t('upload.remove', { name: q.file.name })}
                      disabled={running}
                      onClick={() => setQueue((qs) => qs.filter((x) => x.key !== q.key))}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
          {ready.length > 0 ? (
            <div className={styles.footer}>
              <Button size="small" appearance="primary" disabled={disabled || running} onClick={() => void send(ready)}>
                {t('upload.send', { count: ready.length })}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function StatusText({ q, styles }: { q: QueuedFile; styles: ReturnType<typeof useStyles> }) {
  const { t } = useTranslation();
  switch (statusOf(q)) {
    case 'invalid':
      return <Text size={200} className={styles.bad}>{q.invalid}</Text>;
    case 'duplicate':
      return <Text size={200} className={styles.warn}>{t('upload.duplicate', { name: q.duplicateOf })}</Text>;
    case 'needs-type':
      return <Text size={200} className={styles.warn}>{t('upload.chooseType')}</Text>;
    case 'ready':
      return <Text size={200} className={styles.muted}>{t('upload.ready')}</Text>;
    case 'uploading':
      return <Text size={200} className={styles.muted}>{t('upload.uploading')}</Text>;
    case 'uploaded':
      return <Text size={200} className={styles.good}>{t('upload.uploaded')}</Text>;
    case 'failed':
      return <Text size={200} className={styles.bad}>{t('upload.failed', { error: q.error })}</Text>;
  }
}
